import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getSocket } from '../services/socket';
import { getConversations, getMessages, markAsRead } from '../services/api';
import { useAuth } from './AuthContext';

const ChatContext = createContext();

// ─── Toast Notification Component ────────────────────────────────────────────
function MessageToast({ toasts, onDismiss, onGoToChat }) {
  return (
    <div style={toastStyles.container}>
      {toasts.map((t) => (
        <div key={t.id} style={toastStyles.toast}>
          <div style={toastStyles.avatar}>{t.initials}</div>
          <div style={toastStyles.body}>
            <div style={toastStyles.header}>
              <span style={toastStyles.appName}>BLIXO</span>
              <span style={toastStyles.dot} />
              <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '9px', letterSpacing: '1px' }}>NEW MESSAGE</span>
            </div>
            <div style={toastStyles.name}>{t.name}</div>
            <div style={toastStyles.msg}>{t.message}</div>
          </div>
          <div style={toastStyles.actions}>
            <button
              style={toastStyles.replyBtn}
              onClick={() => { onGoToChat(t.userId); onDismiss(t.id); }}
            >
              Reply
            </button>
            <button style={toastStyles.closeBtn} onClick={() => onDismiss(t.id)}>✕</button>
          </div>
        </div>
      ))}
    </div>
  );
}

const toastStyles = {
  container: {
    position: 'fixed', top: '20px', right: '20px',
    display: 'flex', flexDirection: 'column', gap: '12px',
    zIndex: 99999, maxWidth: '380px', width: 'calc(100vw - 40px)',
  },
  toast: {
    display: 'flex', alignItems: 'center', gap: '14px',
    background: 'linear-gradient(135deg, #0d1f2d, #0a1a28)',
    border: '1px solid rgba(0,255,180,0.4)',
    borderLeft: '4px solid #00ffb4',
    borderRadius: '16px', padding: '18px 20px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.8), 0 0 40px rgba(0,255,180,0.1)',
    animation: 'slideIn 0.35s cubic-bezier(0.34,1.56,0.64,1)',
    fontFamily: "'Exo 2', sans-serif",
  },
  avatar: {
    width: '52px', height: '52px', borderRadius: '14px',
    background: 'linear-gradient(135deg, #003d38, #006655)',
    color: '#00ffb4', display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: '18px', fontWeight: '900',
    flexShrink: 0, border: '2px solid rgba(0,255,180,0.35)',
    boxShadow: '0 0 20px rgba(0,255,180,0.25)',
  },
  body: { flex: 1, minWidth: 0 },
  header: { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '5px' },
  appName: { color: 'rgba(0,255,180,0.7)', fontSize: '9px', fontWeight: '900', letterSpacing: '2px' },
  dot: { width: '5px', height: '5px', borderRadius: '50%', background: '#00ffb4', boxShadow: '0 0 6px #00ffb4', display: 'inline-block', animation: 'pulse 1s infinite' },
  name: {
    color: '#ffffff', fontSize: '16px', fontWeight: '700',
    marginBottom: '4px', letterSpacing: '0.3px',
  },
  msg: {
    color: 'rgba(255,255,255,0.6)', fontSize: '14px',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  actions: { display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0 },
  replyBtn: {
    background: 'linear-gradient(90deg, #00ffb4, #00C8FF)',
    border: 'none', borderRadius: '9px',
    color: '#080c14', fontSize: '13px', fontWeight: '900',
    padding: '8px 16px', cursor: 'pointer',
    fontFamily: "'Exo 2', sans-serif", letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
  },
  closeBtn: {
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '9px', color: 'rgba(255,255,255,0.4)',
    fontSize: '12px', cursor: 'pointer', padding: '6px 12px',
  },
};

// ─── Provider ────────────────────────────────────────────────────────────────
export const ChatProvider = ({ children }) => {
  const { user: admin } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeUser, setActiveUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [typingUsers, setTypingUsers] = useState({});
  const [onlineUsers, setOnlineUsers] = useState({});
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [toasts, setToasts] = useState([]);
  const activeUserRef = useRef(null);
  const toastTimers = useRef({});

  // Request browser notification permission as soon as admin is available
  useEffect(() => {
    if (!admin) return;
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [admin]);

  // Keep ref in sync with state
  useEffect(() => { activeUserRef.current = activeUser; }, [activeUser]);

  const addToast = useCallback((message, conversations) => {
    const conv = conversations.find(c => c.userId?._id === message.userId?.toString() ||
      c.userId?._id === message.senderId?.toString());
    const user = conv?.userId;
    if (!user) return;

    const id = Date.now() + Math.random();
    const initials = user.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';

    setToasts(prev => [...prev.slice(-2), {
      id, userId: user._id, name: user.name,
      initials,
      message: message.type === 'text' ? message.content : `📎 Sent a ${message.type}`,
    }]);

    // Auto dismiss after 5 seconds
    toastTimers.current[id] = setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
      delete toastTimers.current[id];
    }, 5000);
  }, []);

  const dismissToast = useCallback((id) => {
    clearTimeout(toastTimers.current[id]);
    delete toastTimers.current[id];
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const goToChat = useCallback((userId) => {
    setConversations(prev => {
      const conv = prev.find(c => c.userId?._id === userId);
      if (conv?.userId) {
        setActiveUser(conv.userId);
        loadMessages(conv.userId._id);
      }
      return prev;
    });
  }, []);

  const loadConversations = useCallback(async () => {
    try {
      const { data } = await getConversations();
      setConversations(data);
      const onlineMap = {};
      data.forEach((c) => {
        if (c.userId?.isOnline) onlineMap[c.userId._id] = true;
      });
      setOnlineUsers(onlineMap);
      return data;
    } catch (err) {
      console.error('Failed to load conversations:', err);
      return [];
    }
  }, []);

  const loadMessages = useCallback(async (userId) => {
    try {
      setLoadingMessages(true);
      setMessages([]);
      const { data } = await getMessages(userId);
      setMessages(data.messages || []);
      await markAsRead(userId);
      setConversations((prev) =>
        prev.map((c) =>
          c.userId?._id === userId ? { ...c, unreadCount: 0 } : c
        )
      );
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  const selectUser = useCallback((user) => {
    setActiveUser(user);
    if (user) loadMessages(user._id);
    else setMessages([]);
  }, [loadMessages]);

  const sendMessage = useCallback(({ type = 'text', content = '', fileUrl = '', fileName = '', fileSize = 0, replyTo = null }) => {
    const socket = getSocket();
    if (!socket || !activeUserRef.current || !admin) return;
    socket.emit('message:send', {
      senderId:   admin._id,
      senderType: 'admin',
      userId:     activeUserRef.current._id,
      type, content, fileUrl, fileName, fileSize, replyTo,
    });
  }, [admin]);

  useEffect(() => {
    if (!admin) return;
    const socket = getSocket();
    if (!socket) return;

    socket.emit('admin:join');

    let convCache = [];
    loadConversations().then(data => { convCache = data; });

    socket.on('message:received', (message) => {
      const currentActive = activeUserRef.current;

      // Add to messages if this conversation is open
      if (message.senderType === 'user') {
        setMessages((prev) => {
          // Only add if currently viewing this user's chat
          if (currentActive && (
            message.senderId === currentActive._id ||
            message.userId?.toString() === currentActive._id
          )) {
            return [...prev, message];
          }
          return prev;
        });
      } else {
        setMessages((prev) => [...prev, message]);
      }

      // Update sidebar
      setConversations((prev) => {
        const updated = prev.map((c) => {
          const isThisConv = c.userId?._id === message.senderId ||
            c.userId?._id === message.userId?.toString();
          if (isThisConv) {
            const isActiveConv = currentActive?._id === c.userId?._id;
            return {
              ...c,
              lastMessage: message.type === 'text' ? message.content : `📎 ${message.type}`,
              lastMessageTime: message.createdAt,
              unreadCount: message.senderType === 'user' && !isActiveConv
                ? (c.unreadCount || 0) + 1
                : 0,
            };
          }
          return c;
        });
        convCache = updated;
        return updated;
      });

      // Show in-app toast if message from user and not currently viewing their chat
      if (message.senderType === 'user') {
        const currentActive = activeUserRef.current;
        const isViewingThisChat = currentActive &&
          (message.senderId === currentActive._id ||
           message.userId?.toString() === currentActive._id);

        if (!isViewingThisChat) {
          addToast(message, convCache);
          // Browser notification
          if (Notification.permission === 'granted') {
            new Notification('⚡ Blixo — New Message', {
              body: message.type === 'text' ? message.content : message.type === 'image' ? '📷 Sent an image' : '📎 Sent a file',
              icon: '/favicon.ico',
              tag: message.senderId,
            });
          }
        }
      }
    });

    socket.on('typing:start', ({ userId }) => {
      setTypingUsers((prev) => ({ ...prev, [userId]: true }));
    });
    socket.on('typing:stop', ({ userId }) => {
      setTypingUsers((prev) => ({ ...prev, [userId]: false }));
    });
    socket.on('user:online', ({ userId }) => {
      setOnlineUsers((prev) => ({ ...prev, [userId]: true }));
    });
    socket.on('user:offline', ({ userId }) => {
      setOnlineUsers((prev) => ({ ...prev, [userId]: false }));
    });
    socket.on('message:status', ({ messageId, status }) => {
      setMessages((prev) =>
        prev.map((m) => (m._id === messageId ? { ...m, status } : m))
      );
    });

    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }

    return () => {
      socket.off('message:received');
      socket.off('typing:start');
      socket.off('typing:stop');
      socket.off('user:online');
      socket.off('user:offline');
      socket.off('message:status');
    };
  }, [admin]);

  const totalUnread = conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0);

  return (
    <ChatContext.Provider value={{
      conversations, activeUser, messages, typingUsers,
      onlineUsers, loadingMessages, selectUser, sendMessage, loadConversations,
      totalUnread,
    }}>
      {children}
      <MessageToast toasts={toasts} onDismiss={dismissToast} onGoToChat={goToChat} />
      <style>{`@keyframes slideIn { from { transform: translateX(120%); opacity: 0; } to { transform: translateX(0); opacity: 1; } } @keyframes pulse { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:0.4; transform:scale(1.4); } }`}</style>
    </ChatContext.Provider>
  );
};

export const useChat = () => useContext(ChatContext);